import { useQuery, type QueryKey, type QueryObserverOptions } from '@tanstack/react-query';
import { camelizeKeys } from 'receivable-invoices-js/react/utils/camelize-keys';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';
import { apiBaseURL, landingURL } from 'receivable-invoices-js/constants/hosts';

interface Params {
  organizationId?: string;
}

interface APIResponse {
  data: {
    attributes: {
      referral_url?: string;
    };
  };
}

interface CustomOptions {
  onError?: (error: unknown) => void;
}

const URL = `${apiBaseURL}/v5/organizations`;

export function fetchReferralUrlKey(queryParams: Params) {
  return ['referral-url', queryParams] as QueryKey;
}

export async function fetchReferralUrl(params: Params, customOptions?: CustomOptions) {
  const { onError } = customOptions || {};

  try {
    const responseData = await qontoClient<APIResponse>(`${URL}/${params.organizationId}/shared`);

    const camelizedData = camelizeKeys(responseData);

    return camelizedData.data.attributes.referralUrl || landingURL;
  } catch (error) {
    if (onError) {
      onError(error);
      return landingURL;
    }
    return landingURL;
  }
}

export type FetchReferralUrlResponse = Awaited<ReturnType<typeof fetchReferralUrl>>;

export function useFetchReferralUrl(
  params: Params,
  customOptions?: CustomOptions,
  queryOptions?: Partial<QueryObserverOptions<FetchReferralUrlResponse>>
) {
  return useQuery({
    queryKey: fetchReferralUrlKey(params),
    queryFn: () => fetchReferralUrl(params, customOptions),
    enabled: Boolean(params.organizationId) && queryOptions?.enabled,
    ...queryOptions,
  });
}
